export const AuthErrorCode = {
  ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  TENANT_DEACTIVATED: 'TENANT_DEACTIVATED',
  ACCOUNT_NOT_VERIFIED: 'ACCOUNT_NOT_VERIFIED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  AUTH_INVALID_TOKEN: 'AUTH_INVALID_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  SESSION_REVOKED: 'SESSION_REVOKED',
  TENANT_UNDER_MAINTENANCE: 'TENANT_UNDER_MAINTENANCE',
  TENANT_SUSPENDED: 'TENANT_SUSPENDED',
  TENANT_DEPROVISIONED: 'TENANT_DEPROVISIONED',
  TENANT_HOST_MISMATCH: 'TENANT_HOST_MISMATCH',
  TENANT_LINK_MISMATCH: 'TENANT_LINK_MISMATCH',
  WORKSPACE_NOT_FOUND: 'WORKSPACE_NOT_FOUND',
} as const;
export type AuthErrorCodeT = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];

export const HARD_LOGOUT_CODES: ReadonlySet<string> = new Set([
  AuthErrorCode.ACCOUNT_DEACTIVATED,
  AuthErrorCode.ACCOUNT_SUSPENDED,
  AuthErrorCode.TENANT_DEACTIVATED,
  AuthErrorCode.SESSION_REVOKED,
]);

/**
 * Workspace-level 403s that end an active session. TENANT_HOST_MISMATCH belongs
 * here because every tenant is served from the same build on its own subdomain,
 * so continuing under a workspace identity that isn't this user's is worse than
 * dropping the session.
 */
export const TENANT_BLOCK_CODES: ReadonlySet<string> = new Set([
  AuthErrorCode.TENANT_UNDER_MAINTENANCE,
  AuthErrorCode.TENANT_SUSPENDED,
  AuthErrorCode.TENANT_DEPROVISIONED,
  AuthErrorCode.TENANT_HOST_MISMATCH,
]);

export function extractErrorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const e = err as { response?: { data?: { code?: string } } };
  return e.response?.data?.code ?? null;
}

const CODE_MESSAGES: Partial<Record<AuthErrorCodeT, string>> = {
  [AuthErrorCode.ACCOUNT_DEACTIVATED]:
    'Your account has been deactivated. Contact your administrator.',
  [AuthErrorCode.TENANT_DEACTIVATED]:
    'Your organization is currently disabled. Contact your administrator.',
  [AuthErrorCode.ACCOUNT_SUSPENDED]:
    'Your account is suspended. Contact your administrator.',
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]:
    'Your account is not verified. Check your email.',
  [AuthErrorCode.TENANT_UNDER_MAINTENANCE]:
    'Your workspace is undergoing scheduled maintenance. Please try again later.',
  [AuthErrorCode.TENANT_SUSPENDED]:
    'Your workspace has been suspended. Contact your platform administrator.',
  [AuthErrorCode.TENANT_DEPROVISIONED]:
    'Your workspace is no longer available.',
  [AuthErrorCode.TENANT_HOST_MISMATCH]:
    'This workspace address does not belong to your organization. Please sign in at your own workspace URL.',
  // The plain expiry. Says what happened and what to do, because the
  // alternative the user met was "You don't have access to this" — which
  // reads as a permissions problem and sends them to their administrator.
  [AuthErrorCode.TOKEN_EXPIRED]:
    'Your session expired. Please sign in again.',
  [AuthErrorCode.TENANT_LINK_MISMATCH]:
    'This link was issued for a different workspace. Open it from the workspace address in your email.',
  [AuthErrorCode.WORKSPACE_NOT_FOUND]:
    'No workspace is registered at this address. Check the address and try again.',
};

// Accepts either casing: callers pass an API `code` (upper) or a `?reason=`
// query value carried through a redirect (lower).
export function mapReasonToMessage(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return CODE_MESSAGES[reason.toUpperCase() as AuthErrorCodeT] ?? null;
}
